import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useCallback, useEffect, useState } from 'react';
import type { JackpotPool, JackpotProduct, JackpotWonMessage } from '../@types';
import { PRODUCT } from '../types/enums';
import useWebsocket from './useWebsocket';

const useWinningJackpot = (product: JackpotProduct, enabled = true) => {
  const [winningJackpot, setWinningJackpot] = useState<JackpotPool | null>(null);
  const { userId } = useAuth();

  const jackpotUpdater = useCallback(
    (data: JackpotWonMessage[]) => {
      if (!enabled) return;

      data.forEach((message) => {
        const messageData = message[userId];

        if (!messageData || messageData.event !== 'jackpotWinner') {
          return;
        }

        // The feed API's legacy sportsbook websocket event predates the product
        // field. Casino events must identify themselves explicitly, so a missing
        // product can safely be treated as sportsbook during the migration.
        const messageProduct = messageData.payload.product ?? PRODUCT.SPORTSBOOK;

        if (messageProduct !== product) {
          return;
        }

        setWinningJackpot({ ...messageData.payload, product: messageProduct });
      });
    },
    [enabled, product, userId]
  );

  const { joinRoom: joinJackpotRoom } = useWebsocket({
    callback: jackpotUpdater,
  });

  const clearJackpot = useCallback(() => {
    setWinningJackpot(null);
  }, []);

  useEffect(() => {
    if (enabled && userId) {
      joinJackpotRoom(userId);
    }
  }, [enabled, joinJackpotRoom, userId]);

  return {
    clearJackpot,
    winningJackpot,
  };
};

export default useWinningJackpot;
